/**
 * Dashboard Hooks - T138
 *
 * TanStack Query hooks for dashboard data
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

export interface DashboardMetrics {
  revenueThisMonth: number;
  revenueThisYear: number;
  revenueLastMonth: number;
  totalInvoices: number;
  invoicesDraft: number;
  invoicesSent: number;
  invoicesPaid: number;
  invoicesOverdue: number;
  outstandingBalance: number;
  overdueBalance: number;
  totalCustomers: number;
  activeCustomers: number;
}

export interface RevenueByPeriod {
  period: string;
  revenue: number;
  invoiceCount: number;
  prevYearRevenue?: number;
  prevYearInvoiceCount?: number;
}

export interface OutstandingInvoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  grossTotal: number;
  dueDate: string;
  daysOverdue: number;
}

export interface RecentActivity {
  id: string;
  type: 'invoice_created' | 'invoice_paid' | 'invoice_sent' | 'customer_created';
  entityId: string;
  entityName: string;
  amount?: number;
  timestamp: string;
}

// ─────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────

export const dashboardKeys = {
  all: ['dashboard'] as const,
  metrics: () => [...dashboardKeys.all, 'metrics'] as const,
  revenue: (months: number) => [...dashboardKeys.all, 'revenue', months] as const,
  outstanding: (limit: number) => [...dashboardKeys.all, 'outstanding', limit] as const,
  activity: (limit: number) => [...dashboardKeys.all, 'activity', limit] as const,
};

// ─────────────────────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────────────────────

/**
 * Hook to get dashboard metrics
 */
export function useDashboardMetrics() {
  return useQuery({
    queryKey: dashboardKeys.metrics(),
    queryFn: () => api.get<DashboardMetrics>('/dashboard/metrics'),
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000, // Consider fresh for 30s
  });
}

/**
 * Hook to get revenue by period
 */
export function useRevenueByPeriod(months: number = 12) {
  return useQuery({
    queryKey: dashboardKeys.revenue(months),
    queryFn: () => api.get<RevenueByPeriod[]>(`/dashboard/revenue?months=${months}`),
    staleTime: 60000,
  });
}

/**
 * Hook to get outstanding invoices
 */
export function useOutstandingInvoices(limit: number = 10) {
  return useQuery({
    queryKey: dashboardKeys.outstanding(limit),
    queryFn: () => api.get<OutstandingInvoice[]>(`/dashboard/outstanding?limit=${limit}`),
    staleTime: 30000,
  });
}

/**
 * Hook to get recent activity
 */
export function useRecentActivity(limit: number = 10) {
  return useQuery({
    queryKey: dashboardKeys.activity(limit),
    queryFn: () => api.get<RecentActivity[]>(`/dashboard/activity?limit=${limit}`),
    staleTime: 30000,
  });
}

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

export const activityTypeLabels: Record<RecentActivity['type'], string> = {
  invoice_created: 'Rechnung erstellt',
  invoice_sent: 'Rechnung versendet',
  invoice_paid: 'Zahlung erhalten',
  customer_created: 'Kunde angelegt',
};

export const activityTypeIcons: Record<RecentActivity['type'], string> = {
  invoice_created: '📝',
  invoice_sent: '📤',
  invoice_paid: '💰',
  customer_created: '👤',
};
